import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { LoggerService } from '../../../core/services/logger.service';
import { TOAST_TYPE } from '../../../core/services/notification.service';
import {
  CommunicationGroup,
  CreateGroupPayload,
  UpdateGroupPayload,
  GetMessagesResponse,
  MessageFilter,
  MessageGroupSummary,
  SendMessagePayload,
  ReplyMessagePayload,
  ForwardMessagePayload,
  MessageSendOutcome,
  UpdateMessageStatusPayload,
  SystemUser,
  RecipientStatus,
  UnreadCount
} from '../models/messages.model';

/** Service responsible for communication messages and groups. */
@Injectable({ providedIn: 'root' })
export class MessagesService {
  private readonly basePath = 'communication';

  constructor(
    private api: ApiService,
    private logger: LoggerService
  ) {}

  // ── Groups ────────────────────────────────────────────────────────────

  /** Fetch all communication groups, optionally filtered by active status.
   * @param isActive if true → only active, if false → only inactive, undefined → all. */
  getGroups(isActive?: boolean): Observable<CommunicationGroup[]> {
    this.logger.log('MessagesService.getGroups', { isActive });
    const params: Record<string, string | number | boolean> = {};
    if (typeof isActive === 'boolean') {
      params['isActive'] = isActive;
    }
    return this.api.get<{ items: CommunicationGroup[] }>(`${this.basePath}/groups`, params).pipe(
      map(response => response.items ?? [])
    );
  }

  /** Create a new communication group.
   * @param options.suppressErrorToast when true, the global HTTP error
   * interceptor stays silent and the caller takes full responsibility for
   * showing a friendly toast (used by the Group modal to render the
   * translated "duplicate name" message without the raw `code` toast on top). */
  createGroup(
    payload: CreateGroupPayload,
    options?: { suppressErrorToast?: boolean }
  ): Observable<{ groupId: number }> {
    this.logger.log('MessagesService.createGroup', payload);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.post<{ groupId: number }>(`${this.basePath}/groups`, payload, undefined, context);
  }

  /** Update an existing communication group. See {@link createGroup} for `options`. */
  updateGroup(
    id: number,
    payload: UpdateGroupPayload,
    options?: { suppressErrorToast?: boolean }
  ): Observable<{ groupId: number }> {
    this.logger.log('MessagesService.updateGroup', id, payload);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.put<{ groupId: number }>(
      `${this.basePath}/groups/${id}`, payload, undefined, context,
    );
  }

  /** Deactivate a communication group. */
  deactivateGroup(id: number): Observable<void> {
    this.logger.log('MessagesService.deactivateGroup', id);
    return this.api.put<void>(`${this.basePath}/groups/${id}/deactivate`, {});
  }

  /** Reactivate a previously deactivated communication group. See {@link createGroup} for `options`. */
  reactivateGroup(id: number, options?: { suppressErrorToast?: boolean }): Observable<void> {
    this.logger.log('MessagesService.reactivateGroup', id);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.put<void>(`${this.basePath}/groups/${id}/reactivate`, {}, undefined, context);
  }

  // ── Messages ──────────────────────────────────────────────────────────

  /** Search messages with pagination and filters. */
  getMessages(filter: MessageFilter): Observable<GetMessagesResponse> {
    this.logger.log('MessagesService.getMessages', filter);
    return this.api.post<GetMessagesResponse>(`${this.basePath}/messages/search`, filter);
  }

  /** Unread counts (inbox, starred, archived) for the current user (US 59875).
   * No params/body — the backend resolves the user from the bearer token
   * (oid). `toastType: 'info'` opts out of the global HTTP error toast so a
   * failed count never disrupts the header; the caller degrades gracefully. */
  getUnreadCount(): Observable<UnreadCount> {
    this.logger.log('MessagesService.getUnreadCount');
    return this.api.get<UnreadCount>(
      `${this.basePath}/messages/unread-count`,
      undefined,
      { toastType: 'info' }
    );
  }

  /** Send a new message.
   * @param options.suppressErrorToast — when true, silences the global
   * HTTP error interceptor toast. The caller (compose modal) takes
   * responsibility for showing a translated message based on the
   * `error.error.code` returned by the BE — keeps the two-toast bug from
   * recurring when a coded error is also reported by the interceptor
   * (TASK-58395 — Ingrid). Same pattern as `createGroup`/`updateGroup`. */
  sendMessage(
    payload: SendMessagePayload,
    options?: { suppressErrorToast?: boolean },
  ): Observable<MessageSendOutcome> {
    this.logger.log('MessagesService.sendMessage', payload);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.post<MessageSendOutcome>(`${this.basePath}/messages`, payload, undefined, context);
  }

  /** Reply to an existing message. See {@link sendMessage} for `options`. */
  replyMessage(
    payload: ReplyMessagePayload,
    options?: { suppressErrorToast?: boolean },
  ): Observable<MessageSendOutcome> {
    this.logger.log('MessagesService.replyMessage', payload);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.post<MessageSendOutcome>(`${this.basePath}/messages/reply`, payload, undefined, context);
  }

  /** Forward an existing message. See {@link sendMessage} for `options`. */
  forwardMessage(
    payload: ForwardMessagePayload,
    options?: { suppressErrorToast?: boolean },
  ): Observable<MessageSendOutcome> {
    this.logger.log('MessagesService.forwardMessage', payload);
    const context = options?.suppressErrorToast ? buildSuppressToastContext() : undefined;
    return this.api.post<MessageSendOutcome>(`${this.basePath}/messages/forward`, payload, undefined, context);
  }

  /** Update the status flags of a user message (read, favorite, archived, deleted). */
  updateMessageStatus(id: number, payload: UpdateMessageStatusPayload): Observable<void> {
    this.logger.log('MessagesService.updateMessageStatus', id, payload);
    return this.api.put<void>(`${this.basePath}/messages/${id}/status`, payload);
  }

  /** Get recipients of a message with read status (for "Visualizações" modal). */
  getMessageRecipients(messageId: number): Observable<RecipientStatus[]> {
    this.logger.log('MessagesService.getMessageRecipients', messageId);
    return this.api.get<{ recipients: RecipientStatus[] }>(
      `${this.basePath}/messages/${messageId}/recipients`
    ).pipe(map(response => response.recipients ?? []));
  }

  /** Get the groups (with member userIds) the message was sent to — used by
   * the View modal to render the "Para:" tooltip with member names. Lazy
   * because expanding all members for every message on the page made the
   * list query slow when the user had many group messages (TASK-58395 —
   * Ingrid). The list endpoint only returns `groupIds`/`groupNames` now. */
  getMessageGroups(messageId: number): Observable<MessageGroupSummary[]> {
    this.logger.log('MessagesService.getMessageGroups', messageId);
    return this.api.get<{ groups: MessageGroupSummary[] }>(
      `${this.basePath}/messages/${messageId}/groups`
    ).pipe(map(response => response.groups ?? []));
  }

  // ── Users (for member/recipient selection) ──────────────────────────

  /** Search active users by username (min 3 chars, debounced on frontend).
   * Backend excludes the logged-in user by default. Pass `includeSelf: true`
   * when the caller legitimately needs the logged-in user in the list (e.g.
   * the Group Management modal — adding oneself to a group's roster). */
  searchActiveUsers(term: string, options?: { includeSelf?: boolean }): Observable<SystemUser[]> {
    this.logger.log('MessagesService.searchActiveUsers', term, options);
    const params: Record<string, string | boolean> = { term };
    if (options?.includeSelf) {
      params['includeSelf'] = true;
    }
    return this.api.get<{ items: Array<{ userId: string; name: string; email?: string | null }> }>(
      `${this.basePath}/users/search`, params
    ).pipe(map(response => response.items));
  }

  /** Resolve the USUARIO column from an email address. */
  getUserIdByEmail(email: string): Observable<string | null> {
    this.logger.log('MessagesService.getUserIdByEmail', email);
    return this.api.get<{ userId: string | null }>(
      `${this.basePath}/users/by-email`, { email }
    ).pipe(map(response => response.userId));
  }

  /** Resolve USUARIO (PK) a partir do ObjectId do Entra (ENTRA_OBJECT_ID). */
  getUserIdByObjectId(objectId: string): Observable<string | null> {
    this.logger.log('MessagesService.getUserIdByObjectId', objectId);
    return this.api.get<{ userId: string | null }>(
      `${this.basePath}/users/by-objectid`, { objectId }
    ).pipe(map(response => response.userId));
  }
}

/** Suppresses the toast that the global HTTP error interceptor would
 * otherwise show for this request — the caller is expected to render its
 * own friendly message instead.
 *
 * Implementation note: the interceptor only fires for TOAST_TYPE 'error' or
 * 'warning'; any other value falls through the default branch (no toast).
 * We deliberately pass 'info' to opt out without expanding the
 * NotificationType union with a sentinel like 'silent'. */
function buildSuppressToastContext(): HttpContext {
  return new HttpContext().set(TOAST_TYPE, 'info');
}
